import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import * as zod from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Badge,
  Box,
  Button,
  Form,
  FormSection,
  Input,
  ModalFooter,
  Select,
  Table,
  TableCell,
  TableHeader,
  TableHeaders,
  TableRow,
  Text,
  View,
} from '../ui';
import type {
  SpotForm_OrderFragment,
  SpotForm_SpotContractFragment,
  SpotFormQuery,
  SpotFormQueryVariables,
} from '../../graphql';
import { useSpotFormQuery, PaymentUnit } from '../../graphql';
import { isDefined, mapKeyBy } from '../../lib/util';
import { stringToNumber } from '../../lib/zod-util';
import { paymentUnits } from '../../constants/paymentUnits';
import { paymentUnitsToOptions } from '../../lib/form-util';
import PortDataForm, {
  portDataSchema,
  portDataNodesToPortData,
} from '../contracts/PortDataForm';
import { stringToNumOpt } from '../core/util';
import type { ApolloQueryResult } from '@apollo/client';
import BaseContractFields, { baseContractFieldsSchema } from './BaseContract';
import CommissionFields, { commissionFieldsSchema } from './Commission';
import ExtraFreightFields, { extraFreightFieldsSchema } from './ExtraFreights';
import ExtraCostFields, { extraCostFieldsSchema } from './ExtraCosts';
import type { VoyageCharterSchema, VoyageCharterSchemaInput } from './schema';
import { voyageCharterSchema } from './schema';
import { useCurrentUser } from '../../state/zustand';
import { compact } from 'lodash';

type OrderContract =
  | { type: 'inquiry'; voyageCharterId: number | null }
  | { type: 'spot'; voyageCharterId: number | null; onSubsUntil: Date | null }
  | {
      type: 'coa';
      coaId: number;
      voyageCharterId: number | null;
      routeId: number | null;
    };

interface SpotFormProps {
  onClose?: () => void;
  onSave?: (spot: SpotFormSaveType) => void;
  spot: SpotForm_SpotContractFragment | null;
  unsavedOrderIds?: number[]; // Orders that have not yet been committed/saved to the voyage charter
  outgoing: boolean;
  defaultContract?: OrderContract;
}

const spotOrderSchema = zod.object({
  relationId: zod.number().nullable(),
  orderId: zod.number(),
  freightId: zod.number().nullable(),
  freightAmount: zod.number().nullable(),
  freightUnit: zod.nativeEnum(PaymentUnit).nullable(),
});

export type SpotOrderType = zod.infer<typeof spotOrderSchema>;

const spotFormFields = baseContractFieldsSchema
  .merge(commissionFieldsSchema)
  .merge(extraFreightFieldsSchema)
  .merge(extraCostFieldsSchema)
  .extend({
    portData: portDataSchema,
    spotOrders: zod.array(spotOrderSchema),
  });

type SpotFormFields = zod.infer<typeof spotFormFields>;

export type SpotFormSaveType = Omit<VoyageCharterSchema, 'contractDetails'>;

const SpotForm = ({
  onClose,
  onSave,
  spot,
  unsavedOrderIds = [],
  outgoing,
  defaultContract,
}: SpotFormProps): JSX.Element => {
  const organisationId = useCurrentUser()?.organizationId ?? null;

  const { data, refetch } = useSpotFormQuery({
    variables: { unsavedOrderIds, outgoing },
  });

  const orderRelations = useMemo(
    () => spot?.orderRelations.nodes.filter(isDefined) ?? [],
    [spot],
  );

  const assortments = data?.allAssortments?.nodes.filter(isDefined) ?? [];

  const orderIdsWithConflictingRelations = useMemo(() => {
    const conflictingRelations =
      data?.unsavedOrders?.nodes
        .flatMap((order) => order?.orderVoyageCharterRelationsByOrderId.nodes)
        .filter(isDefined) ?? [];

    return new Set(conflictingRelations.map((rel) => rel.orderId));
  }, [data?.unsavedOrders]);

  const orders = useMemo(() => {
    const savedOrders =
      orderRelations.map((or) => or?.orderByOrderId).filter(isDefined) ?? [];
    const unsavedOrders = data?.unsavedOrders?.nodes.filter(isDefined) ?? [];

    return [...savedOrders, ...unsavedOrders];
  }, [orderRelations, data?.unsavedOrders]);

  const ordersById = useMemo(() => mapKeyBy(orders, (o) => o.id), [orders]);
  const coas = compact(data?.allCoaContracts?.nodes);
  const coaId = defaultContract?.type === 'coa' ? defaultContract.coaId : null;
  const coa = useMemo(
    () => (coaId ? (coas.find((coa) => coa.id === coaId) ?? null) : null),
    [coaId, coas],
  );
  const coaRoutes = useMemo(
    () => compact(coa?.coaContractPortsByCoaContractId.nodes),
    [coa],
  );
  const coaAssortments = compact(
    coa?.coaContractAssortmentsByCoaContractId.nodes,
  );
  const coaAssortmentIds = new Set(
    coaAssortments.map((coaAssortment) => coaAssortment.assortmentId),
  );
  const filteredAssortments =
    coaId !== null
      ? assortments.filter((a) => coaAssortmentIds.has(a.id))
      : assortments;
  const coaCommissions = useMemo(() => [], []);

  const savedExtraFreights = useMemo(
    () =>
      spot?.voyageCharterExtraFreightsByVoyageCharterId.nodes.filter(
        isDefined,
      ) ?? [],
    [spot?.voyageCharterExtraFreightsByVoyageCharterId.nodes],
  );

  const savedExtraCosts = useMemo(
    () =>
      spot?.voyageCharterExtraCostsByVoyageCharterId.nodes.filter(isDefined) ??
      [],
    [spot?.voyageCharterExtraCostsByVoyageCharterId.nodes],
  );

  const savedCommissions = useMemo(
    () => spot?.commissionsByVoyageCharterId.nodes.filter(isDefined) ?? [],
    [spot?.commissionsByVoyageCharterId],
  );

  const defaultValues = useMemo<SpotFormFields>(() => {
    const unsavedOrderRelations =
      unsavedOrderIds.map((orderId) => ({
        id: null,
        freightId: null,
        orderId,
        freight: null,
      })) ?? [];

    const spotOrders: SpotOrderType[] = [
      ...orderRelations,
      ...unsavedOrderRelations,
    ].map((or) => {
      const freightAmount = or.freight?.nodes?.[0]?.freightAmount;

      return {
        relationId: or.id,
        orderId: or.orderId,
        freightId: or.freight?.nodes?.[0]?.id ?? null,
        freightAmount: stringToNumOpt(freightAmount) ?? null,
        freightUnit: or.freight?.nodes?.[0]?.freightUnit ?? null,
      };
    });

    const commissions = savedCommissions.map((cm) => ({
      id: cm.id,
      type: cm.type,
      value: +cm.value,
      demurrageApplied: cm.demurrageApplied ?? null,
      extraFreightApplied: cm.extraFreightApplied ?? null,
      bafApplied: cm.bafApplied ?? null,
      organizationId: {
        value: cm.organizationId ?? 0,
        label: cm.organizationByOrganizationId?.name ?? '',
      },
      loadPortId: cm.loadPortId ?? null,
      dischargePortId: cm.dischargePortId ?? null,
      assortmentId: cm.assortmentId ?? 0,
      coaRouteId:
        coaRoutes.find(
          ({ originId, destinationId }) =>
            cm.loadPortId === originId && cm.dischargePortId === destinationId,
        )?.id ?? 0,
    }));

    const extraFreights = savedExtraFreights.map((ef) => ({
      id: ef.id,
      name: ef.name ?? null,
      amount: ef.amount.toString(),
      description: ef.description ?? null,
      type: ef.type ?? null,
      ignoreInPnl: ef.ignoreInPnl ?? null,
    }));

    const extraCosts = savedExtraCosts.map((ec) => ({
      id: ec.id,
      name: ec.name ?? null,
      amount: ec.amount.toString(),
      description: ec.description ?? null,
      type: ec.type ?? null,
    }));

    return {
      id: spot?.id ?? null,
      chartererOrgId: spot?.chartererOrgId
        ? {
            value: spot.chartererOrgId,
            label: spot.organizationByChartererOrgId?.name ?? '',
          }
        : null,
      shipownerOrgId: spot?.shipownerOrgId
        ? {
            value: spot.shipownerOrgId,
            label: spot.organizationByShipownerOrgId?.name ?? '',
          }
        : null,
      vesselId: spot?.vesselId
        ? {
            value: spot.vesselId,
            label: spot.shipByVesselId?.name ?? '',
          }
        : null,
      demurrage: stringToNumOpt(spot?.demurrage) ?? null,
      paymentTrigger: spot?.paymentTrigger ?? null,
      currency: spot?.currency ?? null,
      name: spot?.name ?? null,
      lumpsum: spot && spot.lumpsum ? +spot.lumpsum : null,
      externalId: spot?.externalId ?? null,
      paymentType: spot?.paymentType ?? null,
      paymentTime: spot?.paymentTime ?? null,
      layday: spot?.layday ? new Date(spot.layday) : null,
      cancelling: spot?.cancelling ? new Date(spot.cancelling) : null,
      spotOrders,
      commissions,
      extraFreights,
      extraCosts,
      portData: portDataNodesToPortData(
        spot?.portDataRelations.nodes
          .map((pdRel) => pdRel?.portData)
          .filter(isDefined) ?? [],
      ),
    };
    // Including unsavedOrderIds here will cause infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spot, orderRelations]);

  const form = useForm<SpotFormFields>({
    defaultValues,
    resolver: zodResolver(spotFormFields),
  });

  const { control, handleSubmit, register, reset, watch } = form;

  useEffect(() => reset(defaultValues), [defaultValues, reset]);

  const { fields: spotOrderFields } = useFieldArray({
    control,
    name: 'spotOrders',
    keyName: 'key',
  });

  const [errorMessage, setErrorMessage] = useState('');
  const charterers = data?.charterers?.nodes.filter(isDefined) ?? [];
  const shipowners =
    data?.shipowners?.nodes
      .filter(isDefined)
      .filter((n) => (outgoing ? n.id !== organisationId : true)) ?? [];
  const portAgents = data?.portAgents?.nodes.filter(isDefined) ?? [];
  const organizations = data?.allOrganizations?.nodes.filter(isDefined) ?? [];

  const watchedShipownerOrgId = watch('shipownerOrgId');
  const selectedShipowner = watchedShipownerOrgId?.value
    ? shipowners.find(
        (shipowner) => shipowner.id == watchedShipownerOrgId.value,
      )
    : undefined;
  const vessels =
    selectedShipowner?.shipsByOrganizationId?.nodes.filter(isDefined) ?? [];

  const locationById = useMemo(
    () =>
      orders.reduce<
        Map<number, NonNullable<SpotForm_OrderFragment['loadPort']>>
      >((map, order) => {
        if (order.loadPortId && order.loadPort && !map.has(order.loadPortId)) {
          map.set(order.loadPortId, order.loadPort);
        }
        if (
          order.dischargePortId &&
          order.dischargePort &&
          !map.has(order.dischargePortId)
        ) {
          map.set(order.dischargePortId, order.dischargePort);
        }

        return map;
      }, new Map()),
    [orders],
  );

  const onFormSubmit =
    (shouldClose = false) =>
    (data: SpotFormFields) => {
      // spot orders validation error message
      if (data.spotOrders.find((x) => x.freightAmount && !x.freightUnit)) {
        setErrorMessage('Spot Orders - Freight Unit must be provided.');
        return;
      }

      if (orderIdsWithConflictingRelations.size > 0) {
        return;
      }

      const {
        portData,
        spotOrders,
        commissions,
        extraFreights,
        chartererOrgId,
        shipownerOrgId,
        vesselId,
        layday,
        cancelling,
        extraCosts,
        ...baseContract
      } = data;

      try {
        const dataToParse: Omit<VoyageCharterSchemaInput, 'contractDetails'> = {
          id: spot?.id ?? null,
          baseContract: {
            ...baseContract,
            chartererOrgId: chartererOrgId?.value ?? null,
            shipownerOrgId: shipownerOrgId?.value ?? null,
            vesselId: vesselId?.value ?? null,
            layday: layday?.toISOString() ?? null,
            cancelling: cancelling?.toISOString() ?? null,
          },
          commissions: commissions.map((commission) => ({
            ...commission,
            organizationId: commission.organizationId?.value ?? null,
          })),
          extraFreights,
          extraCosts,
          portData,
          relatedOrders: spotOrders.map((o) => ({
            ...o,
            freightAmount: !baseContract.lumpsum ? o.freightAmount : null,
            coaRouteId: null,
          })),
        };

        const dataToSave = voyageCharterSchema
          .omit({ contractDetails: true })
          .parse(dataToParse);

        onSave?.(dataToSave);
        if (shouldClose && onClose) onClose();
      } catch (error) {
        console.error('Error parsing spot data to save', error);
      }
    };

  const lumpsum = watch('lumpsum');

  return (
    <View flex={1}>
      <Form
        onSubmit={handleSubmit(onFormSubmit(false))}
        flex={1}
        overflowY="scroll"
      >
        <BaseContractFields
          form={form}
          refetch={refetch}
          charterers={charterers}
          shipowners={shipowners}
          vessels={vessels}
        />
        {spotOrderFields.length > 0 && (
          <FormSection title="Orders">
            <Box
              borderWidth={1}
              borderStyle="solid"
              borderColor="neutral.gray_225"
              overflow="auto"
            >
              <Table>
                <thead>
                  <TableHeaders>
                    {unsavedOrderIds.length > 0 ? (
                      <TableHeader width="75"></TableHeader>
                    ) : null}
                    <TableHeader>Order ID</TableHeader>
                    <TableHeader>Commodity</TableHeader>
                    <TableHeader>Quantity</TableHeader>
                    <TableHeader>From</TableHeader>
                    <TableHeader>To</TableHeader>
                    <TableHeader>Freight rate</TableHeader>
                    <TableHeader>Unit</TableHeader>
                  </TableHeaders>
                </thead>
                <tbody>
                  {spotOrderFields.map((field, index) => {
                    const order = ordersById.get(field.orderId);

                    if (!order) return null;

                    return (
                      <TableRow key={field.key} isMuted={index % 2 !== 0}>
                        {unsavedOrderIds.length > 0 ? (
                          <TableCell>
                            {unsavedOrderIds.includes(order.id) ? (
                              <Badge
                                variant={
                                  orderIdsWithConflictingRelations.has(order.id)
                                    ? 'negative'
                                    : 'primary'
                                }
                              >
                                NEW
                              </Badge>
                            ) : null}
                          </TableCell>
                        ) : null}
                        <TableCell>{order.id}</TableCell>
                        <TableCell>{order.assortment?.name}</TableCell>
                        <TableCell>
                          {order.cargoAmount} {order.cargoUnit?.name}
                        </TableCell>
                        <TableCell>{order.loadPort?.name}</TableCell>
                        <TableCell>{order.dischargePort?.name}</TableCell>
                        <TableCell>
                          <input
                            type="hidden"
                            {...register(`spotOrders.${index}.relationId`, {
                              setValueAs: stringToNumber,
                            })}
                            defaultValue={field.relationId?.toString()}
                          />
                          <input
                            type="hidden"
                            {...register(`spotOrders.${index}.freightId`, {
                              setValueAs: stringToNumber,
                            })}
                            defaultValue={field.freightId?.toString()}
                          />
                          <input
                            type="hidden"
                            {...register(`spotOrders.${index}.orderId`, {
                              setValueAs: stringToNumber,
                            })}
                            defaultValue={field.orderId?.toString()}
                          />
                          {!lumpsum ? (
                            <Input
                              {...register(
                                `spotOrders.${index}.freightAmount`,
                                {
                                  setValueAs: stringToNumber,
                                },
                              )}
                              defaultValue={field.freightAmount?.toString()}
                            />
                          ) : (
                            <Text mt={2}>(Total contract freight is used)</Text>
                          )}
                        </TableCell>
                        <TableCell>
                          {!lumpsum && (
                            <Select
                              {...register(`spotOrders.${index}.freightUnit`)}
                              options={paymentUnitsToOptions(paymentUnits)}
                              defaultValue={field.freightUnit?.toString()}
                              required
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </tbody>
              </Table>
            </Box>
            {orderIdsWithConflictingRelations.size > 0 ? (
              <Box mt={4}>
                <Text fontWeight="bold" color="status.error">
                  Some of the orders in this shipment are already attached to
                  another voyage charter in the committed scenario. Please
                  commit the scenario first to properly update the relations.
                </Text>
              </Box>
            ) : null}
          </FormSection>
        )}
        <CommissionFields
          form={form}
          refetch={refetch}
          organizations={organizations}
          assortments={filteredAssortments}
          coaCommissions={coaCommissions}
          coaRoutes={coaRoutes}
          defaultCommissions={defaultValues.commissions}
          enableCoaRouteSelect={coaId !== null}
        />
        <ExtraFreightFields form={form} extraFreights={savedExtraFreights} />
        <ExtraCostFields form={form} extraCosts={savedExtraCosts} />
        <FormProvider {...form}>
          <PortDataForm
            defaultValues={defaultValues.portData ?? {}}
            isReadonly={false}
            portAgents={portAgents}
            locationById={locationById}
          />
        </FormProvider>
      </Form>
      <ModalFooter>
        {onClose && (
          <Button variant="negative" mr={3} onPress={() => onClose()}>
            Cancel
          </Button>
        )}
        <Button
          mr={3}
          disabled={orderIdsWithConflictingRelations.size > 0}
          onPress={handleSubmit(onFormSubmit(false))}
        >
          Save
        </Button>
        {onClose && (
          <Button
            aria-label="save and close"
            onPress={handleSubmit(onFormSubmit(true))}
          >
            Save & Close
          </Button>
        )}
        <div style={{ flexGrow: 1 }} />
        <Text color="status.error" fontWeight="bold">
          {errorMessage}
        </Text>
      </ModalFooter>
    </View>
  );
};

export type SpotFormQueryRefetch = (
  variables?: Partial<SpotFormQueryVariables>,
) => Promise<ApolloQueryResult<SpotFormQuery>>;

export default SpotForm;
